import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Button, message } from 'antd';
import { AssetSuggestion } from '../../types/InvoiceDecisionTypes';
import { getDisplayableErrorMessage, nullSafeGet } from '../../utils/DataAccessUtils';
import {
	isBuyerAllowedToAccess,
	isSupplierAllowedToAccess,
	PERMISSION_NAMES,
} from '../../utils/AuthUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { getAssetSuggestionFields, getSuggestedAssetFieldUpdates } from '../../utils/InvoiceUtils';
import { assetsRestCrudThunks } from '../../thunks/assets_thunks';
import useMountEffect from '../../hooks/useMountEffect';
import { withUserType } from '../withUserType';
import { EnrichAssetModal } from './EnrichAssetModal';

interface InvoiceEnrichmentBannerProps {
	assetSuggestion: AssetSuggestion;
	/** The work order's asset when the invoice already carries it hydrated — saves a read. */
	hydratedAsset?: any;
	userType?: string;
	currentUser?: any;
	companyConfig?: any;
	readAsset?: (id: any) => Promise<any>;
	updateAsset?: (asset: any) => Promise<any>;
}

/**
 * Offers the asset-record updates this invoice implies — a model or serial the invoice printed
 * that the work order's linked asset record is missing or disagrees with.
 *
 * The backend produces these on a clean work-order match and never writes them itself, so this is
 * the only place they can be applied. Gated on MODIFY_ASSETS: someone who cannot edit the asset has
 * no action to take here, so they are not shown the offer — and, because the permission is resolved
 * before the mount effect rather than by a wrapper around the output, they do not pay for the asset
 * read either.
 *
 * The asset is read up front, not on VIEW, because the suggestion is stored on the invoice forever
 * and the backend has no way to mark it handled. Comparing each suggested value against the live
 * record is what stops an already-applied suggestion being offered again on every visit — see
 * getSuggestedAssetFieldUpdates. What it cannot see is a deliberate decline: SKIP closes the modal
 * but the offer returns next visit, because there is nowhere to record the decision. Fixing that
 * needs the backend to accept an acknowledgement.
 */
const InvoiceEnrichmentBannerComponent: React.FC<InvoiceEnrichmentBannerProps> = ({
	assetSuggestion,
	hydratedAsset,
	userType,
	currentUser,
	companyConfig,
	readAsset,
	updateAsset,
}) => {
	const [modalVisible, setModalVisible] = useState(false);
	const [asset, setAsset] = useState<any>(hydratedAsset || null);
	// Until the asset is known there is nothing to compare against, so the banner stays hidden
	// rather than appearing and then vanishing once the record lands.
	const [assetResolved, setAssetResolved] = useState(!!hydratedAsset);
	const [applying, setApplying] = useState(false);

	const assetId = nullSafeGet('assetId', assetSuggestion);
	const hasSuggestion = getAssetSuggestionFields(assetSuggestion).length > 0;

	const canModifyAssets = useMemo(() => {
		const roles = nullSafeGet('roles', currentUser);
		return userType === ROLE_TYPES.SUPPLIER
			? isSupplierAllowedToAccess(PERMISSION_NAMES.MODIFY_ASSETS, companyConfig, roles)
			: isBuyerAllowedToAccess(PERMISSION_NAMES.MODIFY_ASSETS, companyConfig, roles);
	}, [userType, currentUser, companyConfig]);

	useMountEffect(() => {
		if (!canModifyAssets || !hasSuggestion || !assetId || asset || !readAsset) {
			return;
		}
		readAsset(assetId)
			.then((record) => setAsset(record))
			.catch(() => {
				// Show the banner rather than swallow a real suggestion because of a transient read
				// failure; the modal re-reads the asset before it can write anything.
			})
			.finally(() => setAssetResolved(true));
	});

	const fields = useMemo(
		() => getSuggestedAssetFieldUpdates(assetSuggestion, asset),
		[assetSuggestion, asset]
	);

	// Returns the promise so antd's Popconfirm ActionButton owns the pending state: it keeps the
	// popover open with its own spinner until the write settles, instead of closing the instant it
	// is clicked and leaving the user with no feedback.
	const handleEnrich = useCallback(
		(updates) => {
			if (!asset) {
				message.error('The asset record has not loaded yet. Please try again.');
				return Promise.resolve();
			}
			setApplying(true);
			// The assets endpoint takes a whole entity, not a patch, so this is a read-modify-write.
			// Re-read immediately before writing rather than reusing the copy read at mount: a detail
			// page can sit open for a long time, and spreading a stale record would silently revert
			// every field someone else changed in the meantime. A partial update would remove the
			// race entirely, but there is no PATCH route for assets.
			return readAsset(assetId)
				.then((fresh) => updateAsset({ ...(fresh || asset), ...updates }))
				.then((updated) => {
					message.success('Asset updated.');
					setModalVisible(false);
					// Keep the applied values locally so the rows filter out immediately, the same way
					// they will on the next load once the record is re-read.
					setAsset(updated && updated.id ? updated : { ...asset, ...updates });
				})
				.catch((error) =>
					message.error(
						getDisplayableErrorMessage(error, 'Could not update the asset. Please try again.')
					)
				)
				.finally(() => setApplying(false));
		},
		[asset, assetId, readAsset, updateAsset]
	);

	if (!canModifyAssets || !assetResolved || fields.length === 0) {
		return null;
	}

	return (
		// A fragment, not AccessPermissionChecker: that component returns its children unwrapped, so
		// two of them arrive as a keyless array and React warns. The permission is resolved above.
		<>
			<div className="flex flex-row items-center gap-x-s rounded-sm bg-purple-1 px-m py-3">
				<div className="bodyregular flex-1 text-character-primary">
					Enrich Data? We found invoice details that can help update your entities. Please review.
				</div>
				<Button type="primary" onClick={() => setModalVisible(true)}>
					VIEW
				</Button>
			</div>
			{modalVisible ? (
				<EnrichAssetModal
					visible={modalVisible}
					assetSuggestion={assetSuggestion}
					asset={asset}
					applying={applying}
					onEnrich={handleEnrich}
					onCancel={() => setModalVisible(false)}
				/>
			) : null}
		</>
	);
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
	companyConfig: state.company_config.detail,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	readAsset: (id) => dispatch(assetsRestCrudThunks(ownProps.userType).readOne(id)),
	updateAsset: (asset) => dispatch(assetsRestCrudThunks(ownProps.userType).update(asset)),
});

export const InvoiceEnrichmentBanner = withUserType(
	connect(mapStateToProps, mapDispatchToProps)(InvoiceEnrichmentBannerComponent as any) as any
);

export default InvoiceEnrichmentBanner;
